import { ChevronDown, Plus, Search } from 'lucide-react';
import { forwardRef, useEffect, useRef, useState } from 'react';

interface DropdownOption {
  value: string;
  label: string;
  description?: string;
}

interface SearchableDropdownProps {
  label?: string;
  placeholder?: string;
  options: DropdownOption[];
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  maxHeight?: string;
  /** Show a "Create" button when search term has no exact match */
  creatable?: boolean;
  /** Called when user clicks the "Create" button with the current search term */
  onCreateNew?: (searchTerm: string) => void;
}

const SearchableDropdown = forwardRef<HTMLDivElement, SearchableDropdownProps>(
  (
    {
      label,
      placeholder = 'Select option',
      options,
      value,
      onChange,
      error,
      hint,
      required = false,
      disabled = false,
      className = '',
      searchable = true,
      searchPlaceholder = 'Search options...',
      emptyMessage = 'No options found',
      maxHeight = 'max-h-48',
      creatable = false,
      onCreateNew,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const selectedOption = options.find(option => option.value === value);

    // Filter options based on search term
    const filteredOptions = searchable
      ? options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
      : options;

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
          setSearchTerm('');
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    useEffect(() => {
      if (isOpen && searchable && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      }
    }, [isOpen, searchable]);

    const handleSelect = (selectedValue: string) => {
      onChange(selectedValue);
      setIsOpen(false);
      setSearchTerm('');
    };

    const handleCreateNew = () => {
      if (onCreateNew && searchTerm.trim()) {
        onCreateNew(searchTerm.trim());
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const handleToggle = () => {
      if (!disabled) {
        setIsOpen(!isOpen);
        if (!isOpen) {
          setSearchTerm('');
        }
      }
    };

    // Show create button when: creatable enabled, has search text, no exact label match
    const showCreateButton =
      creatable &&
      onCreateNew &&
      searchTerm.trim().length > 0 &&
      !options.some(o => o.label.toLowerCase() === searchTerm.trim().toLowerCase());

    return (
      <div className={`w-full ${className}`} ref={ref}>
        {label && (
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={handleToggle}
            disabled={disabled}
            className={`w-full px-4 py-3 text-left border rounded-lg transition-all duration-200 flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-20 ${
              error
                ? 'border-error-300 focus:border-error-500'
                : 'border-gray-300 focus:border-brand-500'
            } ${
              disabled
                ? 'bg-gray-50 text-gray-500 cursor-not-allowed'
                : 'bg-white hover:border-brand-400'
            } ${isOpen ? 'border-brand-500 ring-2 ring-brand-500 ring-opacity-20' : ''}`}
          >
            <span
              className={`text-sm truncate ${selectedOption ? 'text-gray-900' : 'text-gray-500'}`}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <ChevronDown
              size={16}
              className={`flex-shrink-0 ml-2 text-gray-400 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {isOpen && !disabled && (
            <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
              {/* Search Input */}
              {searchable && (
                <div className="p-3 border-b border-gray-100">
                  <div className="relative">
                    <Search
                      size={14}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      placeholder={searchPlaceholder}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-20 focus:border-brand-500"
                    />
                  </div>
                </div>
              )}

              {/* Options List */}
              <div className={`${maxHeight} overflow-y-auto`}>
                {filteredOptions.length === 0 && !showCreateButton ? (
                  <div className="px-4 py-3 text-sm text-gray-500 text-center">
                    {searchTerm ? `No results found for "${searchTerm}"` : emptyMessage}
                  </div>
                ) : (
                  filteredOptions.map((option, index) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSelect(option.value)}
                      className={`w-full px-4 py-3 text-left text-sm hover:bg-brand-50 hover:text-brand-700 transition-colors ${
                        value === option.value
                          ? 'bg-brand-50 text-brand-700 font-medium'
                          : 'text-gray-900'
                      } ${index === 0 && !searchable ? 'rounded-t-lg' : ''} ${
                        index === filteredOptions.length - 1 && !showCreateButton
                          ? 'rounded-b-lg'
                          : ''
                      }`}
                    >
                      <div>
                        <div className="truncate">{option.label}</div>
                        {option.description && (
                          <div className="text-xs text-gray-500 mt-1 truncate">
                            {option.description}
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>

              {/* Create new option */}
              {showCreateButton && (
                <div className="border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleCreateNew}
                    className="w-full px-4 py-3 text-left text-sm text-brand-600 hover:bg-brand-50 transition-colors flex items-center gap-2 rounded-b-lg"
                  >
                    <Plus size={14} className="flex-shrink-0" />
                    <span>
                      Create &ldquo;{searchTerm.trim()}&rdquo;
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {error && <p className="mt-2 text-sm text-error-600">{error}</p>}
        {hint && !error && <p className="mt-2 text-sm text-gray-500">{hint}</p>}
      </div>
    );
  }
);

SearchableDropdown.displayName = 'SearchableDropdown';

export default SearchableDropdown;
